/**
 * Sync-Queue für offline erfasste Leads.
 *
 * Trigger:
 * - bei `online`-Event (Browser kommt zurück online)
 * - bei manuellem Aufruf (z.B. Sync-Button)
 * - alle 30s als Heartbeat falls in Background offen
 *
 * Pattern:
 * - 4xx (außer 401) = permanent fail, bleibt in Outbox als "blocked"
 *   (User kann es im UI sehen + manuell rerunnen oder löschen)
 * - 5xx + Network = retry mit MAX_ATTEMPTS=5 → danach "blocked"
 * - Multi-Tab: navigator.locks verhindert parallele syncs aus 2 Tabs
 * - Idempotency: client_id wird im payload.data mitgegeben, sodass
 *   bei retry der Backend (mit client_id-dedupe) nicht doppelt anlegt.
 */
import {
  type OutboxLead,
  listOutbox,
  markOutboxError,
  removeFromOutbox,
} from './db';
import { attemptRefresh, getAccessToken } from './api';

const MAX_ATTEMPTS = 5;

let _syncing = false;
const _listeners = new Set<() => void>();

export function onOutboxChange(cb: () => void): () => void {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

function notify(): void {
  for (const cb of _listeners) cb();
}

export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export function isBlocked(entry: OutboxLead): boolean {
  return entry.attempts >= MAX_ATTEMPTS || entry.last_error?.startsWith('permanent_') === true;
}

async function postWithRefresh(path: string, body: unknown, isRetry = false): Promise<Response> {
  const token = getAccessToken();
  const resp = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Authorization: `Bearer ${token ?? ''}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (resp.status === 401 && !isRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) return postWithRefresh(path, body, true);
  }
  return resp;
}

async function uploadCard(leadId: string, card: Blob, mime?: string, isRetry = false): Promise<boolean> {
  const token = getAccessToken();
  const fd = new FormData();
  const ext = mime?.split('/')[1] ?? 'jpg';
  fd.append('file', card, `card.${ext}`);
  const resp = await fetch(`/api/leads/${leadId}/visitenkarte`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Authorization: `Bearer ${token ?? ''}` },
    body: fd,
  });
  if (resp.status === 401 && !isRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) return uploadCard(leadId, card, mime, true);
  }
  return resp.ok;
}

async function syncOne(entry: OutboxLead): Promise<{ ok: boolean; cardOk: boolean }> {
  try {
    // Idempotency: client_id mitgeben damit Backend bei retry nicht doppelt anlegt
    const payload = { ...entry.payload, _client_id: entry.id };
    const resp = await postWithRefresh('/api/leads', payload);
    if (!resp.ok) {
      // 4xx (außer 401) = permanent — Lead bleibt in Outbox als blocked
      if (resp.status >= 400 && resp.status < 500 && resp.status !== 401) {
        await markOutboxError(entry.id, `permanent_${resp.status}`);
        return { ok: false, cardOk: false };
      }
      throw new Error(`HTTP ${resp.status}`);
    }
    const lead = (await resp.json()) as { id: string };
    let cardOk = true;
    if (entry.card_blob) {
      cardOk = await uploadCard(lead.id, entry.card_blob, entry.card_mime);
    }
    await removeFromOutbox(entry.id);
    return { ok: true, cardOk };
  } catch (err) {
    await markOutboxError(entry.id, (err as Error).message);
    return { ok: false, cardOk: false };
  }
}

async function _syncOutboxInner(): Promise<{ synced: number; failed: number; cardLost: number }> {
  if (_syncing) return { synced: 0, failed: 0, cardLost: 0 };
  if (!isOnline()) return { synced: 0, failed: 0, cardLost: 0 };
  _syncing = true;
  let synced = 0;
  let failed = 0;
  let cardLost = 0;
  try {
    const entries = await listOutbox();
    for (const e of entries) {
      if (isBlocked(e)) {
        failed += 1;
        continue;
      }
      const r = await syncOne(e);
      if (r.ok) {
        synced += 1;
        if (!r.cardOk && e.card_blob) cardLost += 1;
      } else {
        failed += 1;
      }
    }
  } finally {
    _syncing = false;
    notify();
  }
  return { synced, failed, cardLost };
}

export async function syncOutbox(): Promise<{ synced: number; failed: number; cardLost: number }> {
  // Multi-Tab-Dedupe via Web-Locks API. Falls nicht supported (Safari < 15.4)
  // fallback auf in-process flag (single-tab).
  if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks) {
    return navigator.locks.request('leadcraft-outbox-sync', { ifAvailable: true }, async (lock) => {
      if (!lock) return { synced: 0, failed: 0, cardLost: 0 };
      return _syncOutboxInner();
    });
  }
  return _syncOutboxInner();
}

let _registered = false;
let _heartbeatId: number | null = null;

export function registerSyncHandlers(): void {
  if (_registered || typeof window === 'undefined') return;
  _registered = true;
  window.addEventListener('online', () => {
    void syncOutbox();
  });
  // Heartbeat alle 30s — fängt Cases wo `online`-Event nicht firet
  _heartbeatId = window.setInterval(() => {
    if (isOnline()) void syncOutbox();
  }, 30_000);
}

export function unregisterSyncHandlers(): void {
  if (_heartbeatId !== null) {
    window.clearInterval(_heartbeatId);
    _heartbeatId = null;
  }
  _registered = false;
}
